import { Action, isAction, Middleware } from 'redux';
import * as Sentry from '@sentry/react';

import { assertUnlessInProduction } from '@paragon/client-tools';
import { ComponentDesign, ProjectSynchronizationPayload } from '@paragon/contract-library';
import { designService } from '@paragon/data-connections';
import {
  componentDesignsDeletedWithInsertions,
  componentDesignToLayoutModel,
  nonSynchronizedLayoutModelLoaded,
  projectSynchronized,
} from '@paragon/layout-models';
import { closeSnackbarsWithMessage, enqueuePermanentError, enqueueWarning } from '@paragon/notification-tools';

import { AsyncThunkActionReturn, AsyncThunkActionVoid, ReduxDispatch, ThunkActionVoid } from '../actions/thunkActions';
import { RootState } from '../index';
import { getSaveStatus } from '../selectors/saveSelectors';
import {
  saveActions,
  setComponentDesignWithoutInvalidation,
  setUnsavedComponentDesignFromMostRecentAnalysisSet,
} from '../slices/componentDesignsSlice';
import { setComponentDesignTimestamps } from '../slices/componentDesignTimestampsSlice';
import { setDimensionSets } from '../slices/dimensionsSlice';
import {
  saveComponentDesignFailure,
  saveComponentDesignRequest,
  saveComponentDesignSuccess,
} from '../slices/saveSlice';
import { withoutCreatingUndoSnapshot } from './editorMiddleware';

const saveFailureMessage =
  'Changes to this project could not be saved. Try reloading the project and removing any invalid objects. ' +
  'Contact support if the problem persists.';

function findComponentDesign(state: RootState, componentDesignGuid: string) {
  const componentDesign = state.componentDesigns.data[componentDesignGuid];
  if (!componentDesign) throw new Error(`Could not find component design with guid ${componentDesignGuid}`);
  return componentDesign;
}

const saveComponentDesign =
  (componentDesignGuid: string, lastUpdatedTimestampOverride: string | null): AsyncThunkActionReturn<ComponentDesign> =>
  async (dispatch, getState) => {
    // You shouldn't be able to make edits when viewing SharedComponentSets because every edit control should be disabled,
    // but just in case, prevent it here.
    if (getState().application.openContext?.type === 'shared-component-set') {
      throw new Error('Attempted to edit a component while viewing a SharedComponentSet');
    }

    const componentDesign = findComponentDesign(getState(), componentDesignGuid);
    const updateResponse = await designService.componentDesigns.postUpdate(
      componentDesign,
      lastUpdatedTimestampOverride ?? undefined,
    );

    const errorUpdatingAssemblyNamesMessage =
      'Error updating assembly names. Ensure all your members are cut correctly.';
    if (updateResponse.errorUpdatingAssemblyNames) {
      const snackbars = getState().notifications.snackbars.map((notification) => notification.message);
      if (!snackbars.includes(errorUpdatingAssemblyNamesMessage)) {
        dispatch(enqueueWarning(errorUpdatingAssemblyNamesMessage));
      }
    }

    if (updateResponse.errorUpdatingDimensions) {
      console.warn('Error updating dimensions.');
    }

    const { componentDesignWithTimestamps, dimensionSet } = updateResponse;
    const { componentDesign: newComponentDesign, timestamps } = componentDesignWithTimestamps;

    dispatch(setComponentDesignTimestamps({ timestamps, allowTimeTraveling: lastUpdatedTimestampOverride != null }));

    if (dimensionSet != null) {
      dispatch(setDimensionSets(dimensionSet));
    }

    return newComponentDesign;
  };

const updateComponentDesignLayoutModelAfterSave =
  (componentDesign: ComponentDesign): ThunkActionVoid =>
  (dispatch, getState) => {
    const componentDesignLayoutModel = componentDesignToLayoutModel(componentDesign);

    const { openContext } = getState().application;
    const projectGuid = openContext?.type === 'project' ? openContext.projectGuid : null;

    if (projectGuid) {
      const synchronizationPayload: ProjectSynchronizationPayload = {
        expiredModelGuids: [componentDesign.guid],
        expiredParentGuids: [],
        incomingModels: [componentDesignLayoutModel],
        incomingInsertions: [],
        incomingTimestamps: [],
      };

      dispatch(projectSynchronized({ [projectGuid]: synchronizationPayload }));
    } else {
      dispatch(nonSynchronizedLayoutModelLoaded(componentDesignLayoutModel));
    }
  };

const saveComponentDesignsForIndividualComponentDesign =
  (componentDesignGuid: string, action: Action): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    let lastUpdatedTimestampOverride = null;
    if (setUnsavedComponentDesignFromMostRecentAnalysisSet.match(action)) {
      lastUpdatedTimestampOverride = action.payload.lastUpdatedTimestamp;
    }

    dispatch(saveComponentDesignRequest({ componentDesignGuid }));
    try {
      const componentDesign = await dispatch(saveComponentDesign(componentDesignGuid, lastUpdatedTimestampOverride));

      if (pendingSaveOperationIds[componentDesignGuid] == null) {
        // setUnsavedComponentDesignFromMostRecentAnalysisSet is only used to set the component design when reverting
        //   active changes.  We can be confident that the server will not make any additional changes to the component
        //   design in this case, thus it's safe to not re-save the component design that was sent back.  It's necessary
        //   to skip this step in order to preserve reference equality.
        if (!setUnsavedComponentDesignFromMostRecentAnalysisSet.match(action)) {
          dispatch(setComponentDesignWithoutInvalidation(withoutCreatingUndoSnapshot({ componentDesign })));
        }

        dispatch(updateComponentDesignLayoutModelAfterSave(componentDesign));

        dispatch(saveComponentDesignSuccess({ componentDesignGuid }));
        reportedComponentDesignGuids.delete(componentDesignGuid);

        // Only take the warning down once nothing else is still failing to save.
        if (getSaveStatus(getState()) !== 'error') {
          dispatch(closeSnackbarsWithMessage(saveFailureMessage));
        }
      }
    } catch (error) {
      dispatch(saveComponentDesignFailure({ componentDesignGuid }));

      if (!getState().notifications.snackbars.some((snackbar) => snackbar.message === saveFailureMessage)) {
        dispatch(enqueuePermanentError(saveFailureMessage));
      }

      // Report the failure as it happens. The notification invites the user to resolve it themselves by removing
      //   whatever the server rejected, so by the time we hear about a project its state no longer shows what went
      //   wrong -- this is our only remaining visibility into the root causes.
      // A component design is reported once for as long as it keeps failing, and again if it fails afresh after saving
      //   successfully. Without that, every edit the user makes to a broken project would send another report.
      if (!reportedComponentDesignGuids.has(componentDesignGuid)) {
        reportedComponentDesignGuids.add(componentDesignGuid);

        Sentry.captureException(error, { extra: { componentDesignGuid } });
      }
    }
  };

const pendingSaveOperationIds: { [componentDesignGuid: string]: string | undefined } = {};
const latestSaveOperations: { [componentDesignGuid: string]: Promise<void> | undefined } = {};
const reportedComponentDesignGuids = new Set<string>();

const handleSaveComponentDesignRequest =
  (action: SaveAction): ThunkActionVoid =>
  (dispatch) => {
    const { componentDesignGuid } = action.payload;
    const latestSaveOperation = latestSaveOperations[componentDesignGuid];

    const createSaveOperation = async () => {
      // If there is an ongoing save operation, wait until it is finished to avoid overlapping requests
      if (latestSaveOperation) {
        const saveOperationId = crypto.randomUUID();
        pendingSaveOperationIds[componentDesignGuid] = saveOperationId;

        await latestSaveOperation;

        // Check whether we are still the most recent save action for the component design. If we are, then clear the
        // pending save action since we're about to start saving. If we are not, then return early since another save
        // action will take precedence.
        if (pendingSaveOperationIds[componentDesignGuid] === saveOperationId) {
          pendingSaveOperationIds[componentDesignGuid] = undefined;
        } else {
          return;
        }
      }

      await dispatch(saveComponentDesignsForIndividualComponentDesign(componentDesignGuid, action));

      if (pendingSaveOperationIds[componentDesignGuid] == null) {
        latestSaveOperations[componentDesignGuid] = undefined;
      }
    };

    latestSaveOperations[componentDesignGuid] = createSaveOperation();
  };

export async function waitForComponentDesignToFinishSaving(componentDesignGuid: string) {
  // Each save action starts a save operation that is synchronously registered with `latestSaveOperations`. Once each
  // save operation resolves, we can check whether a newer save operation has been registered. If not, we should be safe
  // to return.
  while (latestSaveOperations[componentDesignGuid]) {
    await latestSaveOperations[componentDesignGuid];
  }
  assertUnlessInProduction(pendingSaveOperationIds[componentDesignGuid] == null, 'Expect no pending save actions.');
}

type ActionTypeFromArray<TArray extends ReadonlyArray<any>> = {
  [K in number]: ReturnType<TArray[K]>;
}[number];
type SaveAction = ActionTypeFromArray<typeof saveActions>;

export const saveComponentDesignMiddleware: Middleware<{}, RootState, ReduxDispatch> =
  (store) => (next) => (action) => {
    const returnValue = next(action);

    const saveActionTypes = new Set<string>(saveActions.map((action) => action.type));
    if (isAction(action) && saveActionTypes.has(action.type)) {
      store.dispatch(handleSaveComponentDesignRequest(action as SaveAction));
    }

    if (componentDesignsDeletedWithInsertions.match(action)) {
      for (const componentDesignGuid of action.payload.guids) {
        reportedComponentDesignGuids.delete(componentDesignGuid);
      }

      // Deleting a component design drops its save status without saving anything, so it is the one way a failing
      //   component design can stop failing without a successful save to take the warning back down.
      if (
        getSaveStatus(store.getState()) !== 'error' &&
        store.getState().notifications.snackbars.some((snackbar) => snackbar.message === saveFailureMessage)
      ) {
        store.dispatch(closeSnackbarsWithMessage(saveFailureMessage));
      }
    }

    return returnValue;
  };
